'use client';

import {
  CheckboxStylesType,
  ButtonStyleTypes,
  CardStylesType,
  InputStylesType,
  CardBodyStylesType,
  CardFooterStylesType,
  DialogStylesType,
  SelectStylesType,
  TextareaStylesType,
} from '@material-tailwind/react';
// Use the context directly to avoid deepmerge infinite recursion with React 19
// (deepmerge doesn't recognize Symbol(react.transitional.element) and tries to merge React internals)
import { MaterialTailwindTheme } from '@material-tailwind/react/context/theme';
import defaultTheme from '@material-tailwind/react/theme/index';
import type { JSX, ReactNode } from 'react';
import { mergeTheme } from './mergeTheme';

function tw(args: TemplateStringsArray): string {
  return args.join(' ');
}

const themeOverrides = {
  button: {
    defaultProps: {
      color: 'primary' as never,
    },
    valid: {
      colors: ['primary', 'transparent', 'gray', 'neutral'],
    },
    styles: {
      base: {
        initial: {
          display: 'flex justify-center items-center gap-1',
          textTransform: '',
        },
        fullWidth: {
          display: 'flex justify-center items-center gap-1',
        },
      },
      sizes: {
        sm: {
          fontSize: tw`font-semibold text-base md:text-lg`,
          py: tw`py-2`,
          px: tw`px-2`,
          borderRadius: tw`rounded-lg`,
          size: tw`h-10`,
        },
        md: {
          fontSize: tw`font-semibold text-base md:text-lg lg:text-xl`,
          py: tw`py-2 `,
          px: tw`px-2 `,
          borderRadius: tw`rounded-lg `,
          size: tw`h-10 md:h-12`,
        },
      },
      variants: {
        filled: {
          primary: {
            active: tw`active:opacity-85 active:shadow-none`,
            background: tw`bg-primary-800`,
            focus: tw`outline-primary-300 focus:opacity-85 focus:shadow-none focus-visible:outline`,
            hover: tw`hover:shadow-lg hover:shadow-primary-800/40`,
            size: tw`h-10 md:h-12`,
          },
          transparent: {
            active: tw`active:opacity-85`,
            background: tw`bg-transparent`,
            focus: tw`outline-primary-300 focus:opacity-85 focus:shadow-none focus-visible:outline`,
            hover: tw`hover:bg-gray-300`,
            color: tw`text-gray-600`,
          },
        },
        outlined: {
          gray: {
            background: tw`bg-white`,
            border: tw`border border-gray-400`,
            hover: tw`hover:opacity-75`,
            focus: tw`outline-primary-300 focus:ring focus:ring-white/50 focus-visible:outline`,
            active: tw`active:opacity-85`,
          },
          primary: {
            background: tw`bg-white text-primary-800`,
            border: tw`border border-primary-500`,
            hover: tw`hover:opacity-75`,
            focus: tw`outline-primary-300 focus:ring focus:ring-white/50 focus-visible:outline`,
            active: tw`active:opacity-85`,
          },
          neutral: {
            background: tw`bg-white text-gray-600`,
            border: tw`border border-gray-600`,
            hover: tw`hover:opacity-75`,
            focus: tw`outline-primary-300 focus:ring focus:ring-white/50 focus-visible:outline`,
            active: tw`active:opacity-85`,
          },
        },
      },
    },
  } as ButtonStyleTypes,
  card: {
    styles: {
      base: {
        initial: {
          borderRadius: tw`rounded-2xl lg:rounded-3xl`,
          overflow: tw`overflow-hidden`,
        },
      },
      variants: {
        filled: {
          white: {
            color: tw`text-gray-900`,
          },
        },
      },
    },
  } as CardStylesType,
  cardBody: {
    styles: {
      base: {
        p: tw`px-padding/x py-padding/y`,
      },
    },
  } as CardBodyStylesType,
  cardFooter: {
    styles: {
      base: {
        initial: {
          p: tw`px-padding/x py-padding/y`,
        },
        divider: {
          borderWidth: tw`border-t`,
          borderColor: tw`border-gray-300`,
        },
      },
    },
  } as CardFooterStylesType,
  checkbox: {
    defaultProps: {
      color: 'primary' as never,
    },
    valid: {
      colors: ['primary'],
    },
    styles: {
      base: {
        container: {
          flex: tw`shrink-0 self-start`,
        },
        label: {
          fontWeight: tw`font-medium`,
          className: tw`self-start pt-3 text-sm md:text-base`,
        },
      },
      colors: {
        primary: {
          background: tw`checked:bg-primary-800`,
          border: tw`checked:border-primary-800`,
          before: tw`checked:before:bg-primary-800`,
          focus: tw`focus:opacity-85 focus:shadow-none`,
          active: tw`outline-primary-300 focus-visible:outline active:opacity-85 active:shadow-none`,
        },
      },
    },
  } as CheckboxStylesType,
  dialog: {
    styles: {
      base: {
        backdrop: {
          backgroundColor: tw`bg-black/50`,
          backgroundOpacity: tw``,
          backdropFilter: tw``,
          p: tw`p-4`,
        },
      },
      sizes: {
        xs: {
          width: tw`w-full`,
          minWidth: tw``,
          // eslint-disable-next-line tailwindcss/no-arbitrary-value -- from the style guide
          maxWidth: tw`max-w-screen-sm md:max-w-[438px]`,
        },
      },
    },
  } as DialogStylesType,
  input: {
    valid: {
      variants: ['outlined'],
      sizes: ['md'],
    },
    styles: {
      variants: {
        outlined: {
          base: {
            icon: {},
            label: {
              display: tw`hidden`,
            },
            input: {
              placeholder: tw`placeholder:opacity-100 placeholder:text-gray-700`,
              borderColor: tw`!border-gray-400 focus:!border-black`,
            },
          },
          error: {
            input: {
              border: tw`!border-highlights/red-dark focus:!border-highlights/red-dark`,
              bg: tw`!bg-highlights/red-light`,
            },
          },
          sizes: {
            md: {
              container: { height: tw`h-10 md:h-12` },
              input: {
                fontSize: tw`alpha-body-2`,
                px: tw`px-3`,
                py: tw`py-3`,
                borderRadius: tw`rounded-lg`,
              },
              icon: {
                width: tw`size-6`,
                height: tw``,
              },
            },
          },
        },
        standard: {},
        static: {},
      },
    },
  } as InputStylesType,
  select: {
    defaultProps: {
      color: 'gray',
    },
    styles: {
      base: {
        select: { color: tw`text-text/primary` },
        arrow: { initial: { color: tw`text-text/primary` } },
      },
      variants: {
        outlined: {
          base: {
            label: { display: 'hidden' },
          },
          colors: {
            select: {
              gray: {
                close: { borderColor: tw`border-gray-400` },
                open: {
                  borderColor: tw`border-gray-400`,
                  borderTopColor: '',
                },
                withValue: {
                  borderColor: tw`border-gray-400`,
                  borderTopColor: '',
                },
              },
            },
          },
          sizes: {
            md: {
              container: { height: tw`h-10 md:h-12`, fontWeight: '' },
              select: { fontSize: 'alpha-body-2' },
            },
          },
          error: {
            select: {
              initial: {
                background: tw`bg-highlights/red-light`,
              },
              states: {
                close: {
                  borderColor: 'border-highlights/red-dark',
                },
                open: {
                  borderColor: 'border-highlights/red-dark',
                  borderTopColor: '',
                },
                withValue: {
                  borderColor: 'border-highlights/red-dark',
                  borderTopColor: '',
                },
              },
            },
          },
        },
      },
    },
  } as SelectStylesType,
  textarea: {
    valid: {
      variants: ['outlined'],
      sizes: ['md'],
    },
    styles: {
      variants: {
        outlined: {
          base: {
            icon: {},
            label: {
              display: tw`hidden`,
            },
            textarea: {
              placeholder: tw`placeholder:opacity-100 placeholder:text-gray-700`,
              borderColor: tw`!border-gray-400 focus:!border-black`,
            },
          },
          error: {
            textarea: {
              border: tw`!border-highlights/red-dark focus:!border-highlights/red-dark`,
              bg: tw`!bg-highlights/red-light`,
            },
          },
          sizes: {
            md: {
              container: { height: tw`h-10 md:h-12` },
              textarea: {
                fontSize: tw`alpha-body-2`,
                px: tw`px-3`,
                py: tw`py-3`,
                borderRadius: tw`rounded-lg`,
              },
              icon: {
                width: tw`size-6`,
                height: tw``,
              },
            },
          },
        },
        standard: {},
        static: {},
      },
    },
  } as TextareaStylesType,
};

export function MaterialTheme(props: {
  children: NonNullable<ReactNode>;
}): JSX.Element {
  const merged = mergeTheme(defaultTheme, themeOverrides);
  return (
    <MaterialTailwindTheme.Provider value={merged}>
      {props.children}
    </MaterialTailwindTheme.Provider>
  );
}
